import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Download } from 'lucide-react';
import { useProductPhotoTools, type ProductPhotoOptions } from '@/hooks/useProductPhotoTools';
import type { UploadedImage } from '@/pages/ImageToolsHub';
import { BeforeAfterComparison } from './BeforeAfterComparison';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';

interface ProductPhotoToolProps {
  images: UploadedImage[];
}

export const ProductPhotoTool: React.FC<ProductPhotoToolProps> = ({ images }) => {
  const { isProcessing, progress, processedProducts, processImages, downloadAll } = useProductPhotoTools();
  
  const [options, setOptions] = useState<ProductPhotoOptions>({
    whiteBackground: true,
    addShadow: true,
    shadowIntensity: 30,
    addReflection: false,
    reflectionIntensity: 40,
    gridLayout: 'single',
    backgroundColor: '#FFFFFF',
  });

  const selectedImages = images.filter(img => img.selected);

  const handleProcess = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    await processImages(selectedImages, options);
    toast.success(`Processed ${selectedImages.length} product image(s)`);
  };

  const handleDownloadAll = () => {
    downloadAll(processedProducts, 'product');
    toast.success('Downloaded all product images');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Product Photography Settings</CardTitle>
          <CardDescription>
            Professional product photo enhancements
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Background */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>White Background</Label>
              <Switch
                checked={options.whiteBackground}
                onCheckedChange={(checked) => setOptions({ ...options, whiteBackground: checked })}
              />
            </div>

            {options.whiteBackground && (
              <div className="space-y-2">
                <Label>Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={options.backgroundColor}
                    onChange={(e) => setOptions({ ...options, backgroundColor: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    type="text"
                    value={options.backgroundColor}
                    onChange={(e) => setOptions({ ...options, backgroundColor: e.target.value })}
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Shadow */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Add Shadow</Label>
              <Switch
                checked={options.addShadow}
                onCheckedChange={(checked) => setOptions({ ...options, addShadow: checked })}
              />
            </div>

            {options.addShadow && (
              <div className="space-y-2">
                <Label>Shadow Intensity: {options.shadowIntensity}%</Label>
                <Slider
                  value={[options.shadowIntensity]}
                  onValueChange={([value]) => setOptions({ ...options, shadowIntensity: value })}
                  min={0}
                  max={100}
                  step={1}
                />
              </div>
            )}
          </div>

          {/* Reflection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Add Reflection</Label>
              <Switch
                checked={options.addReflection}
                onCheckedChange={(checked) => setOptions({ ...options, addReflection: checked })}
              />
            </div>

            {options.addReflection && (
              <div className="space-y-2">
                <Label>Reflection Intensity: {options.reflectionIntensity}%</Label>
                <Slider
                  value={[options.reflectionIntensity]}
                  onValueChange={([value]) => setOptions({ ...options, reflectionIntensity: value })}
                  min={0}
                  max={100}
                  step={1}
                />
              </div>
            )}
          </div>

          {/* Grid Layout */}
          <div className="space-y-2">
            <Label>Product Grid Layout</Label>
            <Select
              value={options.gridLayout}
              onValueChange={(value: any) => setOptions({ ...options, gridLayout: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Individual Images</SelectItem>
                <SelectItem value="2x2">2x2 Grid</SelectItem>
                <SelectItem value="3x3">3x3 Grid</SelectItem>
                <SelectItem value="4x4">4x4 Grid</SelectItem>
              </SelectContent>
            </Select>
            {options.gridLayout !== 'single' && (
              <p className="text-xs text-muted-foreground">
                Creates a single image with multiple products in a grid
              </p>
            )}
          </div>

          {/* Process Button */}
          <Button
            onClick={handleProcess}
            disabled={isProcessing || selectedImages.length === 0}
            className="w-full"
          >
            {isProcessing ? 'Processing...' : `Process ${selectedImages.length} Image(s)`}
          </Button>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">{Math.round(progress)}%</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {processedProducts.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Processed Products</CardTitle>
                <CardDescription>{processedProducts.length} images enhanced</CardDescription>
              </div>
              <Button onClick={handleDownloadAll}>
                <Download className="h-4 w-4 mr-2" />
                Download All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {processedProducts.map((product) => (
              <BeforeAfterComparison
                key={product.id}
                beforeImage={product.original}
                afterImage={product.processed}
                beforeLabel="Original"
                afterLabel="Enhanced"
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
